import React, { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { createPortal } from "react-dom"
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isToday, addMonths, subMonths, startOfWeek, endOfWeek, isBefore, isAfter } from "date-fns"
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Clock, X, ChevronDown, ChevronUp } from "lucide-react"
import { fr, enUS } from "date-fns/locale"

import { cn } from "../../utils/cn"
import { useI18n } from "../../context/I18nContext"
import { useFollowTriggerPosition } from "../../hooks/useFollowTriggerPosition"

interface DatePickerProps {
  label?: string;
  value?: Date | null | string;
  onChange: (date: Date | null) => void;
  error?: string;
  showErrorText?: boolean;
  allowClear?: boolean;
  helperText?: string;
  placeholder?: string;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  showTimeSelect?: boolean;
  dateFormat?: string;
  className?: string;
  required?: boolean;
}

const DatePickerComponent: React.FC<DatePickerProps> = ({
  label,
  value,
  onChange,
  error,
  showErrorText = true,
  allowClear = true,
  helperText,
  placeholder,
  disabled = false,
  minDate,
  maxDate,
  showTimeSelect = false,
  dateFormat,
  className = "",
  required = false,
}) => {
  const { language } = useI18n()
  const locale = language === 'fr' ? fr : enUS
  const [isOpen, setIsOpen] = useState(false)
  const [currentMonth, setCurrentMonth] = useState(() => {
    // Start with the selected date's month, or today's month, or minDate's month
    if (value) {
      const date = typeof value === 'string' ? new Date(value) : value instanceof Date ? value : null
      if (date) return startOfMonth(date)
    }
    if (minDate) return startOfMonth(minDate)
    return startOfMonth(new Date())
  })
  const [selectedTime, setSelectedTime] = useState<{ hours: number; minutes: number }>({ hours: 9, minutes: 0 })
  const [showMonthDropdown, setShowMonthDropdown] = useState(false)
  const [showYearDropdown, setShowYearDropdown] = useState(false)
  const pickerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)
  const monthDropdownRef = useRef<HTMLDivElement>(null)
  const yearDropdownRef = useRef<HTMLDivElement>(null)

  const computePopupPosition = useCallback((rect: DOMRect, menu: HTMLElement | null = null) => {
    const gap = 8
    const fallbackWidth = showTimeSelect ? 460 : 320
    const fallbackHeight = showTimeSelect ? 400 : 380
    const width = menu && menu.offsetWidth > 0 ? menu.offsetWidth : fallbackWidth
    const height = menu && menu.offsetHeight > 0 ? menu.offsetHeight : fallbackHeight

    const spaceBelow = window.innerHeight - rect.bottom - gap
    const spaceAbove = rect.top - gap
    const placeAbove = spaceBelow < height && spaceAbove > spaceBelow

    let top = placeAbove ? rect.top - height - gap : rect.bottom + gap
    let left = rect.left

    // Viewport clamp (same idea as ContextMenu / More actions)
    left = Math.max(gap, Math.min(left, window.innerWidth - width - gap))
    top = Math.max(gap, Math.min(top, window.innerHeight - height - gap))

    return { top, left }
  }, [showTimeSelect])

  const popupPosition = useFollowTriggerPosition({
    open: isOpen && !disabled,
    triggerRef,
    menuRef: popupRef,
    computePosition: computePopupPosition,
  })

  // Convert string value to Date if needed
  const dateValue = useMemo(() => {
    if (typeof value === 'string' && value) {
      // If it's a date-only string (YYYY-MM-DD), parse it as local date to avoid timezone shift
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-').map(Number)
        return new Date(year, month - 1, day)
      }
      return new Date(value)
    }
    if (value instanceof Date) {
      return value
    }
    return null
  }, [value])

  // Update current month when dateValue changes initially, but only if picker is closed
  // This allows free navigation when picker is open
  useEffect(() => {
    if (!isOpen && dateValue && !isSameMonth(currentMonth, dateValue)) {
      setCurrentMonth(startOfMonth(dateValue))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateValue, isOpen])

  // Helper functions for 12h format (English)
  const to12Hour = (hour24: number): number => {
    if (hour24 === 0) return 12
    if (hour24 > 12) return hour24 - 12
    return hour24
  }

  const to24Hour = (hour12: number, isPM: boolean): number => {
    if (hour12 === 12) return isPM ? 12 : 0
    return isPM ? hour12 + 12 : hour12
  }

  const isPM = (hour24: number): boolean => hour24 >= 12

  // Update time when dateValue changes
  useEffect(() => {
    if (dateValue) {
      setSelectedTime({ hours: dateValue.getHours(), minutes: dateValue.getMinutes() })
    }
  }, [dateValue])

  // Close picker when clicking outside (trigger + portaled popup)
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const inTrigger = Boolean(pickerRef.current?.contains(target))
      const inPopup = Boolean(popupRef.current?.contains(target))

      if (!inTrigger && !inPopup) {
        setIsOpen(false)
        setShowMonthDropdown(false)
        setShowYearDropdown(false)
        return
      }

      if (monthDropdownRef.current && !monthDropdownRef.current.contains(target)) {
        setShowMonthDropdown(false)
      }
      if (yearDropdownRef.current && !yearDropdownRef.current.contains(target)) {
        setShowYearDropdown(false)
      }
    }

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setIsOpen(false)
        setShowMonthDropdown(false)
        setShowYearDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    // Capture so Escape closes the picker without also closing a parent Modal
    document.addEventListener('keydown', handleEscape, true)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape, true)
    }
  }, [isOpen])

  // Default date format
  const defaultDateFormat = showTimeSelect
    ? language === 'fr' 
      ? 'dd/MM/yyyy HH:mm' 
      : 'MM/dd/yyyy hh:mm aa'
    : language === 'fr'
    ? 'dd/MM/yyyy'
    : 'MM/dd/yyyy'

  const formatString = dateFormat || defaultDateFormat

  // Format the selected date for display
  const formattedValue = dateValue && !isNaN(dateValue.getTime())
    ? format(dateValue, formatString, { locale })
    : ''

  // Get month days for calendar
  const weekStartsOn = language === 'fr' ? 1 : 0
  const daysInMonth = useMemo(() => {
    const monthStart = startOfMonth(currentMonth)
    const monthEnd = endOfMonth(currentMonth)
    const calendarStart = startOfWeek(monthStart, { weekStartsOn })
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn })
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd })
  }, [currentMonth, weekStartsOn])

  // Day names
  const dayNames = language === 'fr' 
    ? ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']
    : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

  // Generate month options for dropdown
  const monthOptions = Array.from({ length: 12 }, (_, i) => {
    const monthDate = new Date(2024, i, 1)
    return {
      value: i,
      label: format(monthDate, language === 'fr' ? 'MMMM' : 'MMMM', { locale }),
    }
  })

  // Generate year options for dropdown (current year ± 10 years)
  const currentYear = new Date().getFullYear()
  const yearOptions = Array.from({ length: 21 }, (_, i) => ({
    value: currentYear - 10 + i,
    label: String(currentYear - 10 + i),
  }))

  // Check if date is disabled
  const isDateDisabled = (date: Date): boolean => {
    if (disabled) return true
    if (minDate && isBefore(date, startOfMonth(minDate)) && !isSameDay(date, minDate)) {
      const dayOfMonth = date.getDate()
      const minDay = minDate.getDate()
      if (dayOfMonth < minDay || (dayOfMonth === minDay && isBefore(date, minDate))) {
        return true
      }
    }
    if (minDate && isBefore(date, minDate)) return true
    if (maxDate && isAfter(date, maxDate)) return true
    return false
  }

  // Handle date selection
  const handleDateClick = (date: Date) => {
    if (isDateDisabled(date)) return

    let newDate = new Date(date)
    
    if (showTimeSelect) {
      newDate.setHours(selectedTime.hours, selectedTime.minutes, 0, 0)
    } else {
      // Keep existing time if available, otherwise set to current time
      if (dateValue) {
        newDate.setHours(dateValue.getHours(), dateValue.getMinutes(), dateValue.getSeconds())
      }
    }
    
    onChange(newDate)
    if (!showTimeSelect) {
      setIsOpen(false)
    }
  }

  // Handle time change
  const handleTimeChange = (hours: number, minutes: number) => {
    setSelectedTime({ hours, minutes })
    if (dateValue) {
      const newDate = new Date(dateValue)
      newDate.setHours(hours, minutes, 0, 0)
      onChange(newDate)
    } else {
      // If no date selected, create one for today
      const newDate = new Date()
      newDate.setHours(hours, minutes, 0, 0)
      onChange(newDate)
    }
  }

  // Navigation - allow free navigation between months, only restrict individual date selection
  const goToPreviousMonth = () => {
    const previousMonth = subMonths(currentMonth, 1)
    setCurrentMonth(previousMonth)
  }

  const goToNextMonth = () => {
    const nextMonth = addMonths(currentMonth, 1)
    setCurrentMonth(nextMonth)
  }

  const goToToday = () => {
    const today = new Date()
    setCurrentMonth(startOfMonth(today))
    
    if (!isDateDisabled(today)) {
      let newDate = new Date(today)
      
      // Keep existing time if available and showTimeSelect is not active
      if (!showTimeSelect && dateValue) {
        newDate.setHours(dateValue.getHours(), dateValue.getMinutes(), dateValue.getSeconds())
      } else if (showTimeSelect) {
        newDate.setHours(selectedTime.hours, selectedTime.minutes, 0, 0)
      }
      
      onChange(newDate)
      if (!showTimeSelect) {
        setIsOpen(false)
      }
    }
  }

  // Clear selection
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(null)
    setSelectedTime({ hours: 9, minutes: 0 })
  }

  return (
    <div className={cn("w-full relative", className)} ref={pickerRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      {/* Input Button */}
      <div className="relative">
        <button
          ref={triggerRef}
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={cn(
            "w-full flex items-center justify-between px-3.5 py-2.5 text-base sm:text-sm rounded-xl border transition-colors",
            // Show focus ring on click (mouse) + keyboard
            "focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-0 focus:border-transparent",
            "disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed",
            error
              ? "border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500"
              : "border-gray-200 text-gray-900 placeholder-gray-400 bg-white hover:border-gray-300 hover:shadow-sm",
            // Open state: keep it subtle (avoid a "jump" from adding an extra ring)
            isOpen && "border-primary-500 shadow-sm",
            dateValue && !disabled && "pr-9"
          )}
        >
          <div className="flex items-center flex-1 min-w-0">
            <CalendarIcon className={cn("h-4 w-4 mr-2 flex-shrink-0", error ? "text-red-400" : "text-gray-400")} />
            <span className={cn("truncate", !formattedValue && "text-gray-400")}>
              {formattedValue || placeholder || (language === 'fr' ? 'Sélectionner une date' : 'Select a date')}
            </span>
          </div>
        </button>
        {dateValue && !disabled && allowClear && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-100 transition-colors z-10"
            aria-label={language === 'fr' ? 'Effacer' : 'Clear'}
          >
            <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>

      {/* Calendar Popup — portaled so it escapes modal overflow */}
      {isOpen && !disabled && createPortal(
        <div
          ref={popupRef}
          style={{
            position: 'fixed',
            top: popupPosition?.top ?? 0,
            left: popupPosition?.left ?? 0,
            zIndex: 9999,
            // Hide until first layout position to avoid a 0,0 flash (useLayoutEffect sets it before paint)
            visibility: popupPosition ? 'visible' : 'hidden',
          }}
          className={cn(
            "bg-white rounded-xl shadow-paper border border-gray-100 p-5",
            showTimeSelect ? "min-w-[320px] sm:min-w-[460px]" : "min-w-[320px]"
          )}
        >
          <div
            className={cn(
              showTimeSelect && "flex flex-col sm:flex-row sm:items-stretch gap-0"
            )}
          >
            {/* Date column */}
            <div className={cn("min-w-0 flex flex-col", showTimeSelect && "sm:min-w-[300px] sm:flex-1")}>
              {/* Header - Simple with arrows only */}
              <div className="flex items-center justify-between mb-4">
                <button
                  type="button"
                  onClick={goToPreviousMonth}
                  className="p-2 rounded-lg hover:bg-gray-50 transition-colors group"
                  aria-label="Previous month"
                >
                  <ChevronLeft className="h-4 w-4 text-gray-500 group-hover:text-gray-900 transition-colors" />
                </button>
                
                <h3 className="text-sm font-semibold text-gray-900 capitalize tracking-tight">
                  {format(currentMonth, language === 'fr' ? 'MMMM yyyy' : 'MMMM yyyy', { locale })}
                </h3>
                
                <button
                  type="button"
                  onClick={goToNextMonth}
                  className="p-2 rounded-lg hover:bg-gray-50 transition-colors group"
                  aria-label="Next month"
                >
                  <ChevronRight className="h-4 w-4 text-gray-500 group-hover:text-gray-900 transition-colors" />
                </button>
              </div>

              {/* Calendar Grid */}
              <div>
                {/* Day names */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {dayNames.map((day) => (
                    <div
                      key={day}
                      className="text-xs font-medium text-gray-400 text-center py-2"
                    >
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar days */}
                <div className="grid grid-cols-7 gap-1" key={currentMonth.getTime()}>
                  {daysInMonth.map((day, dayIdx) => {
                    const isCurrentMonth = isSameMonth(day, currentMonth)
                    const isSelected = dateValue && isSameDay(day, dateValue)
                    const isTodayDate = isToday(day)
                    const isDisabled = isDateDisabled(day)

                    return (
                      <button
                        key={dayIdx}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDateClick(day)
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        disabled={isDisabled}
                        className={cn(
                          "h-9 w-9 text-sm rounded-lg transition-colors relative font-medium outline-none focus:outline-none focus-visible:outline-none",
                          !isCurrentMonth && "text-gray-300 opacity-50",
                          isCurrentMonth && !isSelected && !isDisabled && "text-gray-700 hover:bg-gray-50 hover:text-gray-900",
                          isSelected && "bg-primary-500 text-white font-semibold shadow-md hover:bg-primary-600",
                          isTodayDate && !isSelected && "bg-primary-50 text-primary-600 font-semibold hover:bg-primary-100",
                          isDisabled && "text-gray-300 cursor-not-allowed opacity-40"
                        )}
                      >
                        {format(day, 'd')}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Keeps ≥1rem under the days; grows so footer aligns with Valider */}
              <div className="flex-1 min-h-4" aria-hidden />

              {/* Footer with month/year + Today */}
              <div className="pt-3 border-t border-gray-100 flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  {/* Month Selector */}
                  <div className="relative" ref={monthDropdownRef}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowMonthDropdown(!showMonthDropdown)
                        setShowYearDropdown(false)
                      }}
                      className={cn(
                        "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all",
                        "hover:bg-gray-50 hover:text-gray-900 active:scale-95",
                        showMonthDropdown ? "bg-gray-50 text-gray-900" : "text-gray-600"
                      )}
                    >
                      <span className="capitalize">{format(currentMonth, language === 'fr' ? 'MMMM' : 'MMMM', { locale })}</span>
                      <ChevronDown className={cn("h-3 w-3 text-gray-400 transition-all duration-200 flex-shrink-0", showMonthDropdown && "rotate-180 text-gray-600")} />
                    </button>
                    
                    {showMonthDropdown && (
                      <div className="absolute bottom-full left-0 mb-2 bg-white border border-gray-200 rounded-xl shadow-paper py-1.5 z-50 min-w-[150px] max-h-[220px] overflow-y-auto overflow-x-hidden">
                        {monthOptions.map((month) => (
                          <button
                            key={month.value}
                            type="button"
                            onClick={() => {
                              const newMonth = new Date(currentMonth)
                              newMonth.setMonth(month.value)
                              setCurrentMonth(startOfMonth(newMonth))
                              setShowMonthDropdown(false)
                            }}
                            className={cn(
                              "w-full text-left px-3.5 py-2 text-sm rounded-md transition-all mx-1 whitespace-nowrap",
                              "hover:bg-gray-50 active:scale-[0.98]",
                              currentMonth.getMonth() === month.value
                                ? "bg-primary-50 text-primary-600 font-semibold"
                                : "text-gray-700"
                            )}
                          >
                            {month.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Year Selector */}
                  <div className="relative" ref={yearDropdownRef}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowYearDropdown(!showYearDropdown)
                        setShowMonthDropdown(false)
                      }}
                      className={cn(
                        "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-all",
                        "hover:bg-gray-50 hover:text-gray-900 active:scale-95",
                        showYearDropdown ? "bg-gray-50 text-gray-900" : "text-gray-600"
                      )}
                    >
                      {currentMonth.getFullYear()}
                      <ChevronDown className={cn("h-3 w-3 text-gray-400 transition-all duration-200 flex-shrink-0", showYearDropdown && "rotate-180 text-gray-600")} />
                    </button>
                    
                    {showYearDropdown && (
                      <div className="absolute bottom-full left-0 mb-2 bg-white border border-gray-200 rounded-xl shadow-paper py-1.5 z-50 min-w-[110px] max-h-[220px] overflow-y-auto overflow-x-hidden">
                        {yearOptions.map((year) => (
                          <button
                            key={year.value}
                            type="button"
                            onClick={() => {
                              const newMonth = new Date(currentMonth)
                              newMonth.setFullYear(year.value)
                              setCurrentMonth(startOfMonth(newMonth))
                              setShowYearDropdown(false)
                            }}
                            className={cn(
                              "w-full text-left px-3.5 py-2 text-sm rounded-md transition-all mx-1 whitespace-nowrap",
                              "hover:bg-gray-50 active:scale-[0.98]",
                              currentMonth.getFullYear() === year.value
                                ? "bg-primary-50 text-primary-600 font-semibold"
                                : "text-gray-700"
                            )}
                          >
                            {year.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    goToToday()
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="px-3.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-all active:scale-95"
                >
                  {language === 'fr' ? 'Aujourd\'hui' : 'Today'}
                </button>
              </div>
            </div>

            {/* Time column — right of calendar on sm+, below on mobile */}
            {showTimeSelect && (
              <div className="mt-3 pt-3 border-t border-gray-100 sm:mt-0 sm:pt-0 sm:border-t-0 sm:border-l sm:pl-4 sm:ml-4 flex flex-col items-center gap-3 min-w-[7.5rem]">
                <div className="flex flex-col items-center gap-3 w-full">
                <div className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4 text-gray-400" />
                  <h3 className="text-sm font-semibold text-gray-900 tracking-tight">
                    {language === 'fr' ? 'Heure' : 'Time'}
                  </h3>
                </div>
                
                <div className="flex items-center justify-center gap-2">
                  {/* Hours */}
                  <div className="flex flex-col items-center gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        if (language === 'en') {
                          const hour12 = to12Hour(selectedTime.hours)
                          const newHour12 = hour12 === 12 ? 1 : hour12 + 1
                          const newHours = to24Hour(newHour12, isPM(selectedTime.hours))
                          handleTimeChange(newHours, selectedTime.minutes)
                        } else {
                          const newHours = (selectedTime.hours + 1) % 24
                          handleTimeChange(newHours, selectedTime.minutes)
                        }
                      }}
                      className="p-1 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors group"
                    >
                      <ChevronUp className="h-3.5 w-3.5 text-gray-500 group-hover:text-gray-900 transition-colors" />
                    </button>
                    <input
                      type="text"
                      value={language === 'en' 
                        ? to12Hour(selectedTime.hours).toString().padStart(2, '0')
                        : selectedTime.hours.toString().padStart(2, '0')}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '')
                        if (language === 'en') {
                          if (val === '' || (parseInt(val) >= 1 && parseInt(val) <= 12)) {
                            const hour12 = val === '' ? 12 : Math.max(1, Math.min(12, parseInt(val)))
                            const hours = to24Hour(hour12, isPM(selectedTime.hours))
                            handleTimeChange(hours, selectedTime.minutes)
                          }
                        } else {
                          if (val === '' || (parseInt(val) >= 0 && parseInt(val) <= 23)) {
                            const hours = val === '' ? 0 : Math.min(23, parseInt(val))
                            handleTimeChange(hours, selectedTime.minutes)
                          }
                        }
                      }}
                      onBlur={(e) => {
                        if (language === 'en') {
                          const hour12 = Math.max(1, Math.min(12, parseInt(e.target.value) || 12))
                          const hours = to24Hour(hour12, isPM(selectedTime.hours))
                          handleTimeChange(hours, selectedTime.minutes)
                        } else {
                          const hours = Math.max(0, Math.min(23, parseInt(e.target.value) || 0))
                          handleTimeChange(hours, selectedTime.minutes)
                        }
                      }}
                      className="w-11 px-1.5 py-1.5 text-center text-lg font-bold text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all leading-tight hover:border-gray-400"
                      maxLength={2}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (language === 'en') {
                          const hour12 = to12Hour(selectedTime.hours)
                          const newHour12 = hour12 === 1 ? 12 : hour12 - 1
                          const newHours = to24Hour(newHour12, isPM(selectedTime.hours))
                          handleTimeChange(newHours, selectedTime.minutes)
                        } else {
                          const newHours = selectedTime.hours === 0 ? 23 : selectedTime.hours - 1
                          handleTimeChange(newHours, selectedTime.minutes)
                        }
                      }}
                      className="p-1 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors group"
                    >
                      <ChevronDown className="h-3.5 w-3.5 text-gray-500 group-hover:text-gray-900 transition-colors" />
                    </button>
                  </div>

                  <div className="text-xl font-bold text-gray-300 pb-1">:</div>

                  {/* Minutes */}
                  <div className="flex flex-col items-center gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        const newMinutes = (selectedTime.minutes + 15) % 60
                        handleTimeChange(selectedTime.hours, newMinutes)
                      }}
                      className="p-1 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors group"
                    >
                      <ChevronUp className="h-3.5 w-3.5 text-gray-500 group-hover:text-gray-900 transition-colors" />
                    </button>
                    <input
                      type="text"
                      value={selectedTime.minutes.toString().padStart(2, '0')}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '')
                        if (val === '' || (parseInt(val) >= 0 && parseInt(val) <= 59)) {
                          const minutes = val === '' ? 0 : Math.min(59, Math.round(parseInt(val) / 15) * 15)
                          handleTimeChange(selectedTime.hours, minutes)
                        }
                      }}
                      onBlur={(e) => {
                        const minutes = Math.max(0, Math.min(59, Math.round((parseInt(e.target.value) || 0) / 15) * 15))
                        handleTimeChange(selectedTime.hours, minutes)
                      }}
                      className="w-11 px-1.5 py-1.5 text-center text-lg font-bold text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all leading-tight hover:border-gray-400"
                      maxLength={2}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newMinutes = selectedTime.minutes === 0 ? 45 : selectedTime.minutes - 15
                        handleTimeChange(selectedTime.hours, newMinutes)
                      }}
                      className="p-1 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors group"
                    >
                      <ChevronDown className="h-3.5 w-3.5 text-gray-500 group-hover:text-gray-900 transition-colors" />
                    </button>
                  </div>
                </div>

                {language === 'en' && (
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => {
                        const hour12 = to12Hour(selectedTime.hours)
                        const newHours = to24Hour(hour12, false)
                        handleTimeChange(newHours, selectedTime.minutes)
                      }}
                      className={cn(
                        "px-2.5 py-1 text-xs font-semibold rounded-lg transition-all",
                        !isPM(selectedTime.hours)
                          ? "bg-primary-500 text-white shadow-sm"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      )}
                    >
                      AM
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const hour12 = to12Hour(selectedTime.hours)
                        const newHours = to24Hour(hour12, true)
                        handleTimeChange(newHours, selectedTime.minutes)
                      }}
                      className={cn(
                        "px-2.5 py-1 text-xs font-semibold rounded-lg transition-all",
                        isPM(selectedTime.hours)
                          ? "bg-primary-500 text-white shadow-sm"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      )}
                    >
                      PM
                    </button>
                  </div>
                )}

                {/* Desktop quick times — keep mobile uncluttered */}
                <div className="hidden sm:flex flex-col gap-1 w-full pt-1">
                  {[
                    { hours: 9, minutes: 0, fr: '9h', en: '9 AM' },
                    { hours: 12, minutes: 0, fr: 'Midi', en: 'Noon' },
                    { hours: 18, minutes: 0, fr: '18h', en: '6 PM' },
                  ].map((slot) => {
                    const active =
                      selectedTime.hours === slot.hours && selectedTime.minutes === slot.minutes
                    return (
                      <button
                        key={`${slot.hours}:${slot.minutes}`}
                        type="button"
                        onClick={() => handleTimeChange(slot.hours, slot.minutes)}
                        className={cn(
                          "w-full px-2 py-1.5 text-xs font-medium rounded-lg transition-all",
                          active
                            ? "bg-primary-50 text-primary-700 ring-1 ring-primary-200"
                            : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        )}
                      >
                        {language === 'fr' ? slot.fr : slot.en}
                      </button>
                    )
                  })}
                </div>
                </div>

                <div className="flex-1 min-h-4 w-full" aria-hidden />

                {/* Same vertical band as calendar footer + light separator */}
                <div className="w-full pt-3 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      if (!dateValue) {
                        const now = new Date()
                        now.setHours(selectedTime.hours, selectedTime.minutes, 0, 0)
                        onChange(now)
                      }
                      setIsOpen(false)
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="w-full px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-primary-500 text-white hover:bg-primary-600 shadow-sm transition-colors active:scale-95 outline-none focus:outline-none focus-visible:outline-none"
                  >
                    {language === 'fr' ? 'Valider' : 'Done'}
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>,
        document.body
      )}

      {/* Error/Helper Text */}
      {error && showErrorText && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
      {helperText && !error && (
        <p className="mt-1 text-sm text-gray-500">{helperText}</p>
      )}
    </div>
  )
}

export default DatePickerComponent
